import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { withStyles, WithStyles } from '@material-ui/core'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components'
import { HOME_PATH } from 'configs'
import SubmitButton from '../../../../uiKit/buttons/SubmitButton'
import Modal from 'uiKit/Modal'
import { styles } from './style'
import BreadCrumbs from '../../../../uiKit/BreadCrumbs/BreadCrumbs'
import Stepper from './Stepper'
import SetupForm from './SetupForm/SetupForm'
import Loader from 'uiKit/loaders/loader'
import AddQuestions from './Questions/Questions'
import { ChevronLeftIcon } from 'uiKit/icons/Icons'
import { resetForm, validateForm, setCarerOptions, setIsLoading, setFormField } from '../../reducers/supervisionForm'
import {
  resetQuestions,
  addSection,
  addQuestion,
  setExpandedSection,
  setSectionOptions,
  restoreQuestionsState,
  ADD_SECTION,
} from '../../reducers/questions'
import {
  createSupervisionTemplate,
  updateSupervisionTemplate,
  getSupervisionTemplate,
  createTemplateQuestions,
  updateTemplateQuestionsBatch,
  getUsersList,
  getTemplateQuestions,
  getSectionOptions,
} from '../../api/supervision'
import { validateAllQuestionsAndFlag, allQuestionsValidQuickCheck } from './Questions/validationUtils'
import { isQcsBot } from 'constants/qcs'
import { getBotPackage } from 'tabs/home/api/botPackage'
import {
  isBotPackageSupervisionEnabled,
  isDefaultOnlySupervision,
  isQcsStandardSupervisionTemplate,
} from 'tabs/home/api/usageLimits'

const UnsavedModalContent = styled.div`
  h2 {
    margin: 0 0 16px;
    font-family: Lato, sans-serif;
    font-weight: 700;
    font-size: 24px;
    line-height: 100%;
    color: var(--color-oxford-blue);
    word-break: normal;
  }
`

const UnsavedModalBody = styled.p`
  margin: 0;
  font-family: Lato, sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: #616581;
`

const UnsavedModalFooter = styled.div`
  margin: 24px -24px -24px;
  border-top: 1px solid var(--color-ghost-grey);
  padding: 15px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const UnsavedModalActions = styled.div`
  display: flex;
  gap: 8px;
`

interface RouteParams {
  botId: string
  templateId?: string
}

type Props = RouteComponentProps<RouteParams> & WithStyles<typeof styles>

const CreateSupervision: React.FC<Props> = ({ classes, history, match, location }) => {
  const dispatch = useDispatch()
  const botId = match.params.botId
  const activeBot = useSelector((state: any) => state.activeBot)
  const { form } = useSelector((state: any) => state.supervisionForm)
  const questionsBySection = useSelector((state: any) => state.questions.questionsBySection)
  const sections = useSelector((state: any) => state.questions.sections)

  const [activeStep, setActiveStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [templateId, setTemplateId] = useState<number | null>(null)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [fullTemplateData, setFullTemplateData] = useState<any>(null)
  // When user opens questions via "View/ Edit Questions", allow editing even if setup is locked.
  const [questionsEditable, setQuestionsEditable] = useState(false)
  const [questionsSnapshot, setQuestionsSnapshot] = useState<{
    sections: any[]
    questionsBySection: Record<string, any[]>
  } | null>(null)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  // null = checking package; false = blocked (redirect); true = allowed
  const [packageCreateAllowed, setPackageCreateAllowed] = useState<boolean | null>(null)
  // GA supervision (ids 5, 719) remains editable when published; others are view-only.
  const editablePublishedTemplateIds = [5, 719]
  const isDisabled =
    isEditMode && !fullTemplateData?.isDraft && !editablePublishedTemplateIds.includes(templateId)
  const isQuestionsDisabled = isDisabled && !questionsEditable
  const canEditQuestions = !isDisabled || questionsEditable

  const getFrequencyLabel = (frequency: string): string => {
    const frequencyMap = {
      ONE_TIME: 'One time',
      MONTHLY: 'Monthly',
      QUARTERLY: 'Quarterly',
      HALF_YEARLY: '6 months',
      ANNUAL: 'Annually',
      ALTERNATE_MONTHS: 'Alternate months',
    }
    return frequencyMap[frequency] || frequency
  }

  const fetchTemplateData = (botId, templateId) => {
    getSupervisionTemplate(botId, templateId)
      .then(response => {
        const templateData = response.data
        setFullTemplateData(templateData)
        prefillFormWithTemplateData(templateData)
        fetchQuestionsData(botId, templateId)
      })
      .catch(error => {
        setLoading(false)
      })
  }

  const fetchQuestionsData = (botId, templateId) => {
    getTemplateQuestions(botId, templateId)
      .then(response => {
        const questions = response.data || []
        if (questions.length === 0) {
          dispatch(resetQuestions())
          setLoading(false)
          return
        }
        const uniqueSectionIds = [...new Set(questions.map(q => q.sectionId))]
        getSectionOptions(Number(botId))
          .then(sectionsResponse => {
            const sectionOptions = sectionsResponse.data
              .filter(section => section.name.trim() !== 'Other')
              .sort((a, b) => Number(a.code) - Number(b.code))
              .map(section => ({
                value: section.id,
                label: section.name,
              }))
            dispatch(setSectionOptions(sectionOptions))
            const sections = uniqueSectionIds.map(sectionId => {
              const sectionOption = sectionOptions.find(opt => opt.value === sectionId)
              return sectionOption || { value: sectionId, label: `Section ${sectionId}` }
            })
            dispatch(resetQuestions())

            const addSectionWithoutDefaultQuestion = (section, index) => {
              const sectionId = `section-${index}-${section.value}`
              dispatch({
                type: ADD_SECTION,
                payload: {
                  section,
                  skipDefaultQuestion: true,
                },
              })

              return sectionId
            }

            const sectionIds = sections.map((section, index) => {
              return addSectionWithoutDefaultQuestion(section, index)
            })
            questions.forEach(apiQuestion => {
              const sectionIndex = sections.findIndex(s => s.value === apiQuestion.sectionId)
              if (sectionIndex === -1) return

              const sectionId = `section-${sectionIndex}-${apiQuestion.sectionId}`

              const isMultipleChoice = apiQuestion.options && apiQuestion.options.length > 0
              const questionType = isMultipleChoice ? 'multiple_choice' : 'free_text'
              const options = isMultipleChoice
                ? apiQuestion.options.map(opt => ({
                  id: opt.id || `option_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  value: opt.text || '',
                }))
                : [
                  { id: `option_1_${Date.now()}`, value: '' },
                  { id: `option_2_${Date.now()}`, value: '' },
                ]

              const correctAnswers = isMultipleChoice
                ? apiQuestion.options.filter(opt => opt.isCorrect).map(opt => opt.id || '')
                : []
              const questionData = {
                id: apiQuestion.questionId.toString(),
                question: apiQuestion.questionText || '',
                type: questionType,
                isRequired: apiQuestion.requiresAnswer || true,
                options: options,
                correctAnswers: correctAnswers.filter(id => id !== ''),
                minCorrectOptions: apiQuestion.maxAnswers || 1,
              }

              dispatch(addQuestion(questionData, sectionId))
            })

            if (sections.length > 0) {
              const firstSectionId = `section-0-${sections[0].value}`
              dispatch(setExpandedSection(firstSectionId))
            }

            setLoading(false)
          })
          .catch(error => {
            setLoading(false)
          })
      })
      .catch(error => {
        setLoading(false)
      })
  }

  const isPastDate = (dateString: string | null | undefined): boolean => {
    if (!dateString) return false
    const ymdRegex = /^(\d{4})-(\d{2})-(\d{2})/
    const dmyRegex = /^(\d{2})\/(\d{2})\/(\d{4})/
    const ymd = ymdRegex.exec(dateString)
    const dmy = dmyRegex.exec(dateString)
    let d: Date
    if (ymd) {
      const [, y, m, day] = ymd
      d = new Date(Number(y), Number(m) - 1, Number(day))
    } else if (dmy) {
      const [, day, m, y] = dmy
      d = new Date(Number(y), Number(m) - 1, Number(day))
    } else {
      const parsed = new Date(dateString)
      if (isNaN(parsed.getTime())) return false
      d = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
    }
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    return d < today
  }

  const prefillFormWithTemplateData = templateData => {
    if (!templateData) return
    const frequencyOption = {
      value: templateData.frequency,
      label: getFrequencyLabel(templateData.frequency),
    }
    const selectedUsers =
      templateData.users?.map(user => ({
        value: user.id,
        label: user.fullName,
      })) || []
    dispatch(setFormField('name', templateData.name))
    dispatch(setFormField('frequency', frequencyOption))
    dispatch(setFormField('carers', selectedUsers))
    const startDate = templateData.isDraft && isPastDate(templateData.startDate) ? null : templateData.startDate
    const dueDate = templateData.isDraft && isPastDate(templateData.dueDate) ? null : templateData.dueDate
    dispatch(setFormField('startDate', startDate))
    dispatch(setFormField('dueDate', dueDate))
    setTemplateId(templateData.id)
    setIsEditMode(true)
  }

  const fetchUsersList = (botId, templateId) => {
    getUsersList(botId).then(response => {
      dispatch(setCarerOptions([...response.carers_list, ...response.managers_list]))
      if (templateId) {
        fetchTemplateData(botId, templateId)
      }
    })
  }
  useEffect(() => {
    if (!activeBot?.id) {
      return
    }
    const paramTemplateId = match.params.templateId
    const searchParams = new URLSearchParams(location.search || '')
    const qpTemplateId = searchParams.get('templateId')
    const effectiveId = paramTemplateId || qpTemplateId

    // Always clear form + errors so stale validation from a previous visit
    // does not leak into create or another template's edit screen.
    dispatch(resetForm())
    dispatch(resetQuestions())
    setValidationError(null)

    if (effectiveId) {
      setLoading(true)
      setIsEditMode(true)
      setTemplateId(Number(effectiveId))
      fetchUsersList(activeBot.id, Number(effectiveId))
    } else {
      setIsEditMode(false)
      setTemplateId(null)
      fetchUsersList(activeBot.id, null)
      setLoading(false)
    }

    return () => {
      dispatch(resetForm())
      dispatch(resetQuestions())
    }
  }, [activeBot, match.params.templateId, location.search, dispatch])

  const isQcsPackage = isQcsBot(activeBot || botId)

  // Gate deep links: Off blocks create+edit; Default blocks create-only (edit still ok).
  useEffect(() => {
    // CareBrain has no package assignment; a 404 here must not be requested.
    if (!botId || !isQcsPackage) {
      setPackageCreateAllowed(true)
      return undefined
    }
    const isEdit = Boolean(match.params.templateId || new URLSearchParams(location.search || '').get('templateId'))
    const controller = new AbortController()
    setPackageCreateAllowed(null)
    getBotPackage(botId, controller.signal)
      .then(assignment => {
        if (controller.signal.aborted) return
        const supervisionOn = isBotPackageSupervisionEnabled(assignment)
        if (!supervisionOn) {
          setPackageCreateAllowed(false)
          return
        }
        if (!isEdit && isDefaultOnlySupervision(assignment)) {
          setPackageCreateAllowed(false)
          return
        }
        setPackageCreateAllowed(true)
      })
      .catch(() => {
        if (!controller.signal.aborted) setPackageCreateAllowed(true)
      })
    return () => controller.abort()
  }, [botId, isQcsPackage, match.params.templateId, location.search])

  useEffect(() => {
    if (packageCreateAllowed === false) {
      history.replace(`${HOME_PATH}/bot/${botId}/settings/surveysSupervision`)
    }
  }, [packageCreateAllowed, history, botId])

  const isFormComplete = () => {
    const isOneTime = form?.frequency?.value === 'ONE_TIME'
    const hasBasicFields = form.name && form.frequency && form.carers && form.carers.length > 0
    const hasDates = !isOneTime || (form.startDate && form.dueDate)
    return hasBasicFields && hasDates
  }

  const canSubmit = useMemo(() => {
    const formComplete = isFormComplete()
    if (activeStep === 1) {
      return formComplete && allQuestionsValidQuickCheck(questionsBySection)
    }
    return formComplete
  }, [activeStep, form, questionsBySection])

  const normalizeDateForCompare = (dateString: string | null | undefined): string | null => {
    if (!dateString) return null
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return null
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
  }

  const hasFormChanges = useMemo(() => {
    if (!fullTemplateData || !form) return false

    if ((form.name || '') !== (fullTemplateData.name || '')) return true
    if ((form.frequency?.value || null) !== (fullTemplateData.frequency || null)) return true

    const currentUserIds = (form.carers || []).map(carer => Number(carer.value)).sort((a, b) => a - b)
    const originalUserIds = (fullTemplateData.users || []).map(user => Number(user.id)).sort((a, b) => a - b)
    if (currentUserIds.length !== originalUserIds.length) return true
    if (currentUserIds.some((id, index) => id !== originalUserIds[index])) return true

    const isOneTime = form.frequency?.value === 'ONE_TIME' || fullTemplateData.frequency === 'ONE_TIME'
    if (isOneTime) {
      const originalStartDate =
        fullTemplateData.isDraft && isPastDate(fullTemplateData.startDate) ? null : fullTemplateData.startDate
      const originalDueDate =
        fullTemplateData.isDraft && isPastDate(fullTemplateData.dueDate) ? null : fullTemplateData.dueDate
      if (normalizeDateForCompare(form.startDate) !== normalizeDateForCompare(originalStartDate)) return true
      if (normalizeDateForCompare(form.dueDate) !== normalizeDateForCompare(originalDueDate)) return true
    }

    return false
  }, [form, fullTemplateData])

  const normalizeQuestionsForCompare = useCallback((questionsMap: Record<string, any[]>) => {
    const normalized: Record<string, any[]> = {}
    Object.keys(questionsMap || {})
      .sort()
      .forEach(sectionId => {
        normalized[sectionId] = (questionsMap[sectionId] || []).map(question => {
          const { errors, _validationError, ...rest } = question || {}
          return rest
        })
      })
    return normalized
  }, [])

  const captureQuestionsSnapshot = useCallback(() => {
    setQuestionsSnapshot({
      sections: JSON.parse(JSON.stringify(sections)),
      questionsBySection: JSON.parse(JSON.stringify(questionsBySection)),
    })
  }, [sections, questionsBySection])

  const hasQuestionsChanges = useMemo(() => {
    if (!questionsSnapshot || isQuestionsDisabled) return false
    const current = JSON.stringify({
      sections,
      questionsBySection: normalizeQuestionsForCompare(questionsBySection),
    })
    const original = JSON.stringify({
      sections: questionsSnapshot.sections,
      questionsBySection: normalizeQuestionsForCompare(questionsSnapshot.questionsBySection),
    })
    return current !== original
  }, [questionsSnapshot, sections, questionsBySection, isQuestionsDisabled, normalizeQuestionsForCompare])

  const goBackToSetup = () => {
    setActiveStep(0)
    setQuestionsEditable(false)
    setValidationError(null)
    setShowUnsavedModal(false)
    setQuestionsSnapshot(null)
  }

  const handleBack = () => {
    // Unsaved-changes prompt only for published edit; create and draft edit can leave freely.
    const isPublishedEdit = isEditMode && !!fullTemplateData && !fullTemplateData.isDraft
    if (isPublishedEdit && hasQuestionsChanges) {
      setShowUnsavedModal(true)
      return
    }
    goBackToSetup()
  }

  const handleLeaveAnyway = () => {
    if (questionsSnapshot) {
      dispatch(
        restoreQuestionsState({
          sections: questionsSnapshot.sections,
          questionsBySection: questionsSnapshot.questionsBySection,
        }),
      )
    }
    goBackToSetup()
  }

  const handleContinueEditing = () => {
    setShowUnsavedModal(false)
  }

  const handleNext = () => {
    if (isFormComplete()) {
      setQuestionsEditable(false)
      captureQuestionsSnapshot()
      setActiveStep(prev => prev + 1)
      dispatch(validateForm())
    } else {
      dispatch(validateForm())
    }
  }

  const handleViewQuestion = () => {
    if (!isFormComplete()) {
      dispatch(validateForm())
      return
    }

    setValidationError(null)
    setQuestionsEditable(true)
    captureQuestionsSnapshot()
    setActiveStep(1)
  }

  const handleUpdate = () => {
    if (!isFormComplete()) {
      dispatch(validateForm())
      setValidationError('Please complete all required fields in the setup form')
      return
    }

    setValidationError(null)
    setLoading(true)
    const payload = {
      botId: Number(botId),
      name: form.name,
      frequency: form.frequency.value,
      userIds: form.carers.map(carer => carer.value),
      isDraft: false,
      startDate: formatDateWithCurrentTime(form.startDate) || fullTemplateData?.startDate,
      dueDate: formatDateWithCurrentTime(form.dueDate) || fullTemplateData?.dueDate,
    }

    updateSupervisionTemplate(Number(botId), Number(templateId), payload)
      .then(() => {
        setLoading(false)
        navigateToSupervisionList()
      })
      .catch(() => {
        setLoading(false)
      })
  }

  const handleSubmit = () => {
    if (!isFormComplete()) {
      dispatch(validateForm())
      setValidationError('Please complete all required fields in the setup form')
      return
    }

    const inlineValid = validateAllQuestionsAndFlag(questionsBySection, dispatch)
    if (!inlineValid) return

    setValidationError(null)
    onSaveTemplate(false)
  }

  const handleSaveDraft = () => {
    if (activeStep === 1) {
      const inlineValid = validateAllQuestionsAndFlag(questionsBySection, dispatch)
      if (!inlineValid) return

      setValidationError(null)
      onSaveQuestionsDraft()
    } else {
      onSaveTemplateDraft()
    }
  }

  const handleSaveAndExit = () => {
    setShowUnsavedModal(false)
    if (fullTemplateData && !fullTemplateData.isDraft) {
      handleSubmit()
    } else {
      handleSaveDraft()
    }
  }

  const formatDateWithCurrentTime = (dateString: string) => {
    if (!dateString) return null
    const date = new Date(dateString)
    const now = new Date()
    date.setUTCHours(now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds(), now.getUTCMilliseconds())
    return date.toISOString()
  }
  const onSaveQuestionsDraft = () => {
    setLoading(true)
    const basePayload = {
      botId: Number(botId),
      name: form.name,
      frequency: form.frequency.value,
      userIds: form.carers.map(carer => carer.value),
      isDraft: true,
      startDate: formatDateWithCurrentTime(form.startDate),
      dueDate: formatDateWithCurrentTime(form.dueDate),
    }
    const apiCall =
      isEditMode && templateId
        ? updateSupervisionTemplate(Number(botId), templateId, {
          ...basePayload,
          startDate: fullTemplateData?.startDate || new Date().toISOString(),
          dueDate: fullTemplateData?.dueDate || new Date().toISOString(),
        })
        : createSupervisionTemplate(basePayload)

    apiCall
      .then(response => {
        const hasQuestions = Object.values(questionsBySection).some(
          questions => Array.isArray(questions) && questions.length > 0,
        )
        if (hasQuestions) {
          onFormatPayloadQuestions(response.data.id)
        } else {
          setLoading(false)
          navigateToSupervisionList()
        }
      })
      .catch(error => {
        setLoading(false)
      })
  }

  const onSaveTemplateDraft = () => {
    if (isFormComplete()) {
      setValidationError(null)
      onSaveTemplate(true)
    }
    dispatch(validateForm())
  }

  const onSaveTemplate = (isDraft = false) => {
    setLoading(true)
    const basePayload = {
      botId: Number(botId),
      name: form.name,
      frequency: form.frequency.value,
      userIds: form.carers.map(carer => carer.value),
      isDraft: isDraft,
      startDate: formatDateWithCurrentTime(form.startDate),
      dueDate: formatDateWithCurrentTime(form.dueDate),
    }
    const apiCall =
      isEditMode && templateId
        ? updateSupervisionTemplate(Number(botId), templateId, {
          ...basePayload,
          startDate: formatDateWithCurrentTime(form.startDate) || fullTemplateData?.startDate,
          dueDate: formatDateWithCurrentTime(form.dueDate) || fullTemplateData?.dueDate,
        })
        : createSupervisionTemplate(basePayload)

    apiCall
      .then(response => {
        if (isDraft) {
          onFormatPayloadQuestions(response.data.id)
          // navigateToSupervisionList()
        } else {
          const hasQuestions = Object.values(questionsBySection).some(
            questions => Array.isArray(questions) && questions.length > 0,
          )

          if (hasQuestions) {
            onFormatPayloadQuestions(response.data.id)
          } else {
            setLoading(false)
            navigateToSupervisionList()
          }
        }
      })
      .catch(error => {
        setLoading(false)
      })
  }

  const navigateToSupervisionList = () => {
    dispatch(resetForm())
    dispatch(resetQuestions())
    history.push(`${HOME_PATH}/bot/${botId}/settings/surveysSupervision`)
  }

  const onFormatPayloadQuestions = (templateId: number) => {
    const formattedQuestions = []
    let sequenceNo = 1
    sections.forEach((section, index) => {
      const sectionId = `section-${index}-${section.value}`
      const sectionQuestions = questionsBySection[sectionId] || []
      sectionQuestions.forEach((question, qIndex) => {
        const formattedQuestion: any = {
          questionText: question.question,
          minAnswers: question.type === 'free_text' ? 1 : 1,
          maxAnswers: question.type === 'free_text' ? 1 : question.minCorrectOptions,
          requiresAnswer: question.isRequired,
          sectionId: Number(section.value),
          type: question.type,
        }
        const isLastSection = index === sections.length - 1
        const isLastQuestionInLastSection = isLastSection && qIndex === sectionQuestions.length - 1

        if (!isLastQuestionInLastSection) {
          formattedQuestion.sequenceNo = sequenceNo
          sequenceNo++
        }
        if (isEditMode && question.id && !question.id.startsWith('question_')) {
          formattedQuestion.questionId = Number(question.id)
        }
        if (question.options && question.options.length > 0 && question.type !== 'free_text') {
          const mappedOptions = question.options.map(option => {
            const optionData = {
              isCorrect: question.correctAnswers?.includes(option.id) || false,
              text: option.value,
            }
            if (isEditMode && option.id && typeof option.id === 'number') {
              return {
                ...optionData,
                id: Number(option.id),
              }
            }

            return optionData
          })
          formattedQuestion.options = mappedOptions
        }
        formattedQuestions.push(formattedQuestion)
      })
    })
    const payload = {
      questions: formattedQuestions,
    }

    const apiCall =
      isEditMode && templateId
        ? updateTemplateQuestionsBatch(Number(botId), templateId, payload)
        : createTemplateQuestions(Number(botId), templateId, payload)
    apiCall
      .then(response => {
        setLoading(false)
        navigateToSupervisionList()
      })
      .catch(error => {
        setLoading(false)
      })
  }
  if (loading || packageCreateAllowed !== true) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
        <Loader />
      </div>
    )
  }

  // Next (step 0) only needs a complete form.
  // Submit (step 1): published edit requires form/questions changes;
  // create and draft edit are enabled as soon as the form is valid.
  const requiresChangesToSubmit = isEditMode && !!fullTemplateData && !fullTemplateData.isDraft
  const canClickPrimaryCta =
    activeStep === 0
      ? isFormComplete()
      : canSubmit && (!requiresChangesToSubmit || hasFormChanges || hasQuestionsChanges)
  const submitOpacity = canClickPrimaryCta ? 1 : 0.5
  const submitOnClick = canClickPrimaryCta
    ? activeStep === 0
      ? handleNext
      : handleSubmit
    : undefined
  const showEditPublishedActions = activeStep === 0 && isEditMode && !!fullTemplateData && !fullTemplateData.isDraft
  const isQcs = isQcsBot(activeBot)
  const isStandardSupervision = isQcsStandardSupervisionTemplate(fullTemplateData)
  const hideViewEditQuestions = isQcs && isStandardSupervision

  return (
    <div>
      <div className={classes.container}>
        <BreadCrumbs
          items={[
            { label: 'Supervision', onClick: navigateToSupervisionList },
            { label: isEditMode ? 'Edit template' : 'Create new' },
          ]}
        />

        <div className={classes.formLayout}>
          <Stepper uiClasses={classes} activeStep={activeStep} />
          <div className={classes.formContent}>
            {activeStep === 0 && <SetupForm isDisabled={isDisabled} />}
            {activeStep === 1 && <AddQuestions isDisabled={isQuestionsDisabled} botId={Number(botId)} />}
            {validationError && <p style={{ color: 'var(--color-text-error)', margin: '10px' }}>{validationError}</p>}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: activeStep === 1 ? 'space-between' : 'flex-end',
                marginBottom: 30,
                padding: '30px 30px 0 30px',
                borderTop: '1px solid var(--color-issabeline-grey)',
              }}>
              {activeStep === 1 && (
                <SubmitButton
                  styles={{ width: 194, color: 'var(--color-button-primary)' }}
                  type="cancel"
                  icon={<ChevronLeftIcon width={12} height={20} color="var(--color-button-primary)" />}
                  title="Back"
                  onClick={handleBack}
                  disabled={loading}
                />
              )}
              <div className={classes.buttonGroup}>
                {canEditQuestions && (!isEditMode || !!fullTemplateData?.isDraft) && (
                  <SubmitButton
                    styles={{ width: 194, opacity: submitOpacity }}
                    type="primary_with_border"
                    title="Save as Draft"
                    onClick={canSubmit ? handleSaveDraft : undefined}
                    disabled={loading}
                  />
                )}
                {showEditPublishedActions ? (
                  <>
                    {![5, 719].includes(Number(templateId)) && !hideViewEditQuestions && (
                      <SubmitButton
                        styles={{ marginLeft: 30, width: 180 }}
                        type="primary_with_border"
                        title="View/ Edit Questions"
                        disabled={loading}
                        onClick={handleViewQuestion}
                      />
                    )}
                    <SubmitButton
                      styles={{
                        marginLeft: 10,
                        width: 150,
                        opacity: hasFormChanges ? 1 : 0.5,
                        ...(!hasFormChanges
                          ? {
                              backgroundColor: 'var(--color-button-primary)',
                              borderColor: 'var(--color-button-primary)',
                              border: '1px solid var(--color-button-primary)',
                            }
                          : null),
                      }}
                      type="primary"
                      title="Update"
                      disabled={loading || !hasFormChanges}
                      onClick={hasFormChanges ? handleUpdate : undefined}
                    />

                  </>
                ) : (
                  canEditQuestions && (
                    <SubmitButton
                      styles={{
                        marginLeft: 30,
                        width: 194,
                        opacity: submitOpacity,
                        ...(!canClickPrimaryCta
                          ? {
                              backgroundColor: 'var(--color-button-primary)',
                              borderColor: 'var(--color-button-primary)',
                              border: '1px solid var(--color-button-primary)',
                            }
                          : null),
                      }}
                      type="primary"
                      title={
                        activeStep === 0
                          ? 'Next'
                          : isEditMode && fullTemplateData && !fullTemplateData.isDraft
                            ? 'Save changes'
                            : 'Submit'
                      }
                      disabled={loading || !canClickPrimaryCta}
                      onClick={submitOnClick}
                    />
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={showUnsavedModal}
        onClose={handleContinueEditing}
        title=""
        classes={{ paper: classes.unsavedModalPaper }}>
        <UnsavedModalContent>
          <h2>You Have Unsaved Changes</h2>
          <UnsavedModalBody>
            You have unsaved changes to this question. If you leave without saving, all the changes you made will be
            lost. Save your changes to keep them.
          </UnsavedModalBody>
          <UnsavedModalFooter>
            <SubmitButton type="warning" title="Leave Anyway" onClick={handleLeaveAnyway} />
            <UnsavedModalActions>
              <SubmitButton type="primary_with_border" title="Continue Editing" onClick={handleContinueEditing} />
              <SubmitButton type="primary" title="Save and exit" onClick={handleSaveAndExit} />
            </UnsavedModalActions>
          </UnsavedModalFooter>
        </UnsavedModalContent>
      </Modal>
    </div>
  )
}

const StyledComponent = withStyles(styles, { withTheme: true })(CreateSupervision)
export default withRouter(StyledComponent)
